import { LitElement, html, css, nothing } from 'lit';
import { StoreMixin } from '../store.js';
import { api } from '../api.js';
import { navigate } from '../router.js';
import { count, mediumDate, money, percent, since } from '../lib/format.js';
import {
  activitySentence,
  flagMeta,
  quarterHours,
  sourceWords,
  staleSentence,
  TASKS_SKIPPED,
} from '../lib/deal.js';

const KIND_WORDS = { note: 'Note', email: 'Email', call: 'Call', meeting: 'Meeting', task: 'Task' };
const label = (s) => String(s ?? '').replace(/_/g, ' ');

/**
 * One deal's estimate (Estimate Tool 13; Phase E17) — `/estimate/deals/:id`.
 *
 * What a HubSpot deal would take: its scope in hours and dollars, its schedule, and who would
 * do the work, worked out from the deal's amount, its words, and the closed projects it most
 * resembles. Every figure on this screen says where it came from, because a deal is thin
 * evidence and the reader has to be able to tell a number that came from the amount from one
 * that came from three vaguely similar projects.
 *
 * Four things the design is particular about:
 *
 * **A version shows what it said.** Generating stores a new version; the old ones stay and can
 * be read here. When the deal or its activity has moved since, the screen says so and names
 * what moved — it never quietly shows a number the evidence no longer supports.
 *
 * **The kick-off date is the one thing set here.** HubSpot has no such property. The date a
 * planner sets is stored on the deal, outlives every regeneration, and is what the forecast
 * reads as the deal's kickoff (13.5). Setting it regenerates, because the schedule moved.
 *
 * **The analogs are the argument.** The table shows each comparable project with the axes it
 * scored on, so a planner who disagrees with the hours can see which project pulled them.
 *
 * **Nothing here names a person as an assignment.** The people under each role are who the
 * analogs point at — who logged the hours on that kind of work — and are advice for the
 * producer, not a staffing decision (Estimate Tool 8.3).
 */
class EstimateDeal extends StoreMixin(LitElement) {
  static properties = {
    route: { type: Object },
    _data: { state: true },
    _viewing: { state: true },
    _loadError: { state: true },
    _error: { state: true },
    _busy: { state: true },
    _kickoff: { state: true },
    _note: { state: true },
    _showAllActivity: { state: true },
  };

  constructor() {
    super();
    this.route = null;
    this._data = null;
    this._viewing = null;
    this._loadError = null;
    this._error = null;
    this._busy = false;
    this._kickoff = '';
    this._note = '';
    this._showAllActivity = false;
  }

  static styles = css`
    :host {
      display: block;
    }
    .card {
      background: var(--surface);
      border: 1px solid var(--border-soft);
      border-radius: var(--radius-card);
      box-shadow: var(--shadow-card);
      padding: 20px 24px 18px;
      margin-bottom: 18px;
    }
    .eyebrow {
      font-family: var(--font-eyebrow);
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      color: var(--ink-muted);
    }
    h2 {
      margin: 6px 0 4px;
      font-size: 20px;
      color: var(--ink-strong);
    }
    h3 {
      margin: 0 0 10px;
      font-size: 14px;
      color: var(--ink-strong);
    }
    .sub,
    .muted {
      color: var(--ink-muted);
      font-size: 13px;
    }
    .sub {
      margin: 0 0 14px;
      line-height: 1.5;
    }
    .facts {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
      gap: 10px 18px;
      margin: 12px 0 8px;
    }
    .fact .k {
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: var(--ink-muted);
      font-weight: 700;
    }
    .fact .v {
      font-size: 17px;
      font-weight: 700;
      color: var(--ink-strong);
      font-variant-numeric: tabular-nums;
    }
    .fact .s {
      font-size: 12px;
      color: var(--ink-muted);
    }
    .actions {
      display: flex;
      gap: 10px 16px;
      align-items: center;
      flex-wrap: wrap;
      margin-top: 12px;
    }
    button.primary,
    button.secondary {
      font: inherit;
      font-size: 13.5px;
      font-weight: 700;
      padding: 8px 16px;
      border-radius: 6px;
      cursor: pointer;
    }
    button.primary {
      color: var(--surface);
      background: var(--ink-strong);
      border: 1px solid var(--ink-strong);
    }
    button.secondary {
      color: var(--ink-strong);
      background: var(--control-bg);
      border: 1px solid var(--control-border);
    }
    button[disabled] {
      opacity: 0.55;
      cursor: not-allowed;
    }
    .banner {
      padding: 10px 14px;
      border-radius: 8px;
      font-size: 13px;
      margin: 10px 0;
      background: var(--status-caution);
      color: var(--status-caution-ink, var(--ink-strong));
    }
    .banner.note {
      background: var(--surface-2);
      color: var(--ink);
    }
    .error {
      color: var(--status-overloaded);
      font-weight: 600;
      font-size: 13px;
      margin: 8px 0;
    }
    .grid2 {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
      gap: 18px;
    }
    .scroll {
      overflow-x: auto;
    }
    table {
      border-collapse: collapse;
      width: 100%;
      font-size: 13px;
    }
    th,
    td {
      text-align: left;
      padding: 6px 10px;
      border-bottom: 1px solid var(--border-soft);
      vertical-align: top;
    }
    th {
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: var(--ink-muted);
      font-weight: 700;
      white-space: nowrap;
    }
    td.num,
    th.num {
      text-align: right;
      font-variant-numeric: tabular-nums;
      white-space: nowrap;
    }
    tfoot td {
      font-weight: 700;
      border-top: 2px solid var(--border);
    }
    table.tasks tr.phase td {
      font-weight: 700;
      border-top: 1px solid var(--border);
    }
    table.tasks td.task {
      padding-left: 22px;
    }
    table.tasks tr.other td.task {
      color: var(--ink-muted);
      font-style: italic;
    }
    .people {
      margin: 2px 0 0;
      padding: 0;
      list-style: none;
      font-size: 12.5px;
    }
    .people li span {
      color: var(--ink-muted);
    }
    .flags {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin: 8px 0 0;
      padding: 0;
      list-style: none;
    }
    .flag {
      font-size: 11.5px;
      padding: 2px 8px;
      border-radius: var(--radius-pill);
      border: 1px solid var(--border);
      color: var(--ink);
    }
    .flag.warn {
      background: var(--status-caution);
      color: var(--status-caution-ink, var(--ink-strong));
      border-color: transparent;
    }
    .form {
      display: grid;
      grid-template-columns: auto minmax(180px, 1fr) auto auto;
      gap: 8px 12px;
      align-items: center;
      margin-top: 10px;
    }
    input[type='date'],
    input[type='text'] {
      font: inherit;
      font-size: 13px;
      padding: 6px 8px;
      border: 1px solid var(--control-border);
      border-radius: 6px;
      background: var(--control-bg);
      color: var(--ink-strong);
    }
    .activity {
      margin: 0;
      padding: 0;
      list-style: none;
    }
    .activity li {
      padding: 8px 0;
      border-bottom: 1px solid var(--border-soft);
      font-size: 13px;
    }
    .activity .head {
      display: flex;
      gap: 10px;
      font-size: 12px;
      color: var(--ink-muted);
    }
    .activity .head b {
      color: var(--ink-strong);
    }
    .activity .preview {
      white-space: pre-wrap;
      margin-top: 2px;
    }
    .versions {
      margin: 0;
      padding: 0;
      list-style: none;
      font-size: 13px;
    }
    .versions li {
      padding: 4px 0;
    }
    .versions button {
      font: inherit;
      font-size: 13px;
      color: var(--ink-strong);
      background: none;
      border: 0;
      padding: 0;
      cursor: pointer;
      text-decoration: underline;
    }
    .versions li.current button {
      text-decoration: none;
      font-weight: 700;
      cursor: default;
    }
    .back {
      font-size: 13px;
      color: var(--ink-muted);
      text-decoration: none;
    }
    .back:hover {
      text-decoration: underline;
    }
    .scores {
      font-size: 12.5px;
      color: var(--ink-muted);
    }
  `;

  get _id() {
    return this.route?.params?.id ?? null;
  }

  get _canWrite() {
    return ['planner', 'admin'].includes(this.appState.user?.effective_role);
  }

  connectedCallback() {
    super.connectedCallback();
    this._boot();
  }

  updated(changed) {
    if (changed.has('route') && changed.get('route') && this._id !== this._loadedId) this._boot();
  }

  async _boot() {
    if (!this._id) return;
    this._loadedId = this._id;
    this._viewing = null;
    try {
      this._data = await api.get(`/estimate/deals/${this._id}`);
      this._loadError = null;
      this._kickoff = this._data.deal.kickoff?.date ?? '';
      this._note = this._data.deal.kickoff?.note ?? '';
    } catch (err) {
      this._loadError = `Could not load the deal: ${err.message}`;
    }
  }

  async _generate() {
    this._busy = true;
    this._error = null;
    try {
      this._data = await api.post(`/estimate/deals/${this._id}/generate`, {});
      this._viewing = null;
    } catch (err) {
      this._error = `Could not generate: ${err.message}`;
    } finally {
      this._busy = false;
    }
  }

  async _saveKickoff(clear = false) {
    this._busy = true;
    this._error = null;
    try {
      this._data = await api.put(`/estimate/deals/${this._id}/kickoff`, {
        kickoff_date: clear ? null : this._kickoff || null,
        note: clear ? null : this._note || null,
      });
      this._viewing = null;
      this._kickoff = this._data.deal.kickoff?.date ?? '';
      this._note = this._data.deal.kickoff?.note ?? '';
    } catch (err) {
      this._error = `Could not save the kick-off date: ${err.message}`;
    } finally {
      this._busy = false;
    }
  }

  async _view(version) {
    if (version.id === this._data.estimate?.id) {
      this._viewing = null;
      return;
    }
    try {
      const { version: full } = await api.get(`/estimate/deals/${this._id}/versions/${version.id}`);
      this._viewing = full;
    } catch (err) {
      this._error = `Could not load version ${version.version}: ${err.message}`;
    }
  }

  _go(event, path) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    navigate(path);
  }

  // -------------------------------------------------------------------------------------------

  _header(deal) {
    const e = deal.estimate;
    return html`
      <div class="card">
        <a class="back" href="/estimate/deals" @click=${(ev) => this._go(ev, '/estimate/deals')}
          >← Deals</a
        >
        <div class="eyebrow" style="margin-top:8px">Deal estimate</div>
        <h2>${deal.name}</h2>
        <p class="sub">
          ${deal.client ?? 'No company'} · ${deal.stage_name ?? deal.stage_id} ·
          ${deal.amount === null ? 'no amount' : money(deal.amount)}
          ${deal.expected_close_date ? html` · closes ${mediumDate(deal.expected_close_date)}` : nothing}
          ${deal.owner_name ? html` · ${deal.owner_name}` : nothing}
          ${deal.source_url ? html` · <a href=${deal.source_url} target="_blank" rel="noopener">Open in HubSpot</a>` : nothing}
          <br />
          ${activitySentence(e ? (this._data.estimate?.result?.activity?.counted ?? this._counted()) : this._counted())}
          logged against it
          ${deal.activity.last_at ? html`, the last ${since(deal.activity.last_at)}` : nothing}.
          ${deal.status !== 'open' ? html` This deal is <b>${deal.status}</b>; its estimate is history.` : nothing}
        </p>
        ${e?.stale ? html`<div class="banner">${staleSentence(e.stale_reasons)} Generate again to read the deal as it stands.</div>` : nothing}
        ${this._error ? html`<p class="error">${this._error}</p>` : nothing}
        <div class="actions">
          ${
            this._canWrite
              ? html`<button
                  class="primary"
                  ?disabled=${this._busy || deal.status === 'archived'}
                  @click=${() => this._generate()}
                >
                  ${e ? 'Generate again' : 'Generate estimate'}
                </button>`
              : nothing
          }
          ${
            e
              ? html`<span class="muted">
                  Version ${e.version} · ${since(e.generated_at)}
                  ${e.generated_by ? html`by ${e.generated_by}` : e.trigger === 'sync' ? 'by the sync' : nothing}
                </span>`
              : html`<span class="muted">No estimate yet.</span>`
          }
        </div>
      </div>
    `;
  }

  _counted() {
    const c = { note: 0, email: 0, call: 0, meeting: 0, task: 0 };
    for (const a of this._data?.activities ?? []) c[a.kind] = (c[a.kind] ?? 0) + 1;
    return c;
  }

  _kickoffCard(deal) {
    const k = deal.kickoff;
    return html`
      <div class="card">
        <div class="eyebrow">Kick-off</div>
        <h3>When the work would start</h3>
        <p class="sub">
          HubSpot has no kick-off date. The one set here is what the forecast reads as this deal's
          kickoff, and what the schedule below starts from; without one, the close date plus the
          service lag stands in.
        </p>
        ${
          k
            ? html`<p>
                <b>${mediumDate(k.date)}</b>
                <span class="muted"
                  >set
                  ${since(k.set_at)}${k.set_by ? html` by ${k.set_by}` : nothing}${k.note ? html` · ${k.note}` : nothing}</span
                >
              </p>`
            : html`<p class="muted">Not set.</p>`
        }
        ${
          this._canWrite
            ? html`<div class="form">
                <label for="kickoff">Date</label>
                <input
                  id="kickoff"
                  type="date"
                  .value=${this._kickoff}
                  @input=${(e) => (this._kickoff = e.target.value)}
                />
                <input
                  type="text"
                  placeholder="Why this date (optional)"
                  .value=${this._note}
                  @input=${(e) => (this._note = e.target.value)}
                />
                <span>
                  <button
                    class="secondary"
                    ?disabled=${this._busy || !this._kickoff}
                    @click=${() => this._saveKickoff(false)}
                  >
                    Save
                  </button>
                  ${k ? html`<button class="secondary" ?disabled=${this._busy} @click=${() => this._saveKickoff(true)}>Clear</button>` : nothing}
                </span>
              </div>`
            : nothing
        }
      </div>
    `;
  }

  _scope(r) {
    const s = r.scope;
    return html`
      <div class="card">
        <div class="eyebrow">Scope</div>
        <h3>${s.hours === null ? 'No hour figure' : `${quarterHours(s.hours)} of work`}</h3>
        <div class="facts">
          <div class="fact">
            <div class="k">Hours</div>
            <div class="v">${s.hours === null ? '—' : quarterHours(s.hours)}</div>
            <div class="s">${sourceWords(s.hours_basis)}</div>
          </div>
          <div class="fact">
            <div class="k">Amount</div>
            <div class="v">${s.amount === null ? '—' : money(s.amount)}</div>
            <div class="s">${sourceWords(s.amount_basis)}</div>
          </div>
          <div class="fact">
            <div class="k">Blended rate</div>
            <div class="v">${money(s.blended_rate)}/h</div>
            <div class="s">
              ${s.blended_rate_source === 'rate_sheet' ? `${r.rate_sheet?.name ?? 'the default rate sheet'}, by role` : 'the pipeline setting'}
            </div>
          </div>
          <div class="fact">
            <div class="k">The analogs say</div>
            <div class="v">
              ${s.hours_from_analogs === null ? '—' : quarterHours(s.hours_from_analogs)}
            </div>
            <div class="s">
              ${s.hours_band?.p25 !== null && s.hours_band?.p25 !== undefined ? `${quarterHours(s.hours_band.p25)} to ${quarterHours(s.hours_band.p75)}` : 'no hours on record'}
              ${s.analog_hours_ratio !== null ? ` · ${s.analog_hours_ratio}× the amount's` : ''}
            </div>
          </div>
          <div class="fact">
            <div class="k">Size</div>
            <div class="v">
              ${s.size_tier ? label(s.size_tier) : s.scope_tier ? label(s.scope_tier) : '—'}
            </div>
            <div class="s">
              ${s.size_tier ? `website ladder, ${quarterHours(s.size_template_hours)} template` : s.scope_tier ? 'forecast scope tier' : ''}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  _typeCard(r) {
    const t = r.type;
    const scores = Object.entries(t.scores ?? {}).sort((a, b) => b[1] - a[1]);
    return html`
      <div class="card">
        <div class="eyebrow">Kind of work</div>
        <h3>${t.value ? label(t.value) : 'Not known'}</h3>
        <p class="sub">
          ${sourceWords(t.source)}${t.tied ? ' — the words argue for two kinds equally' : ''}.
        </p>
        ${
          scores.length
            ? html`<p class="scores">
                Words for: ${scores.map(([k, v]) => `${label(k)} (${v})`).join(', ')}
              </p>`
            : nothing
        }
        ${
          t.services_mentioned?.length
            ? html`<p class="scores">
                Services mentioned:
                ${t.services_mentioned.map((s) => `${label(s.service)} (${s.score})`).join(', ')}
              </p>`
            : nothing
        }
        ${
          r.flags?.length
            ? html`<ul class="flags">
                ${r.flags.map((f) => {
                  const m = flagMeta(f);
                  return html`<li class="flag ${m.level}" title=${m.text}>${m.label}</li>`;
                })}
              </ul>`
            : nothing
        }
      </div>
    `;
  }

  _schedule(r) {
    const s = r.schedule;
    const services = [...new Set(s.weeks.flatMap((w) => Object.keys(w.by_service)))];
    return html`
      <div class="card">
        <div class="eyebrow">Schedule</div>
        <h3>
          ${
            s.kickoff_date && s.delivery_date
              ? `${mediumDate(s.kickoff_date)} to ${mediumDate(s.delivery_date)}`
              : 'Nothing anchors a schedule'
          }
        </h3>
        <div class="facts">
          <div class="fact">
            <div class="k">Kick-off</div>
            <div class="v">${s.kickoff_date ? mediumDate(s.kickoff_date) : '—'}</div>
            <div class="s">
              ${sourceWords(s.kickoff_source)}${s.kickoff_lag_weeks !== null && s.kickoff_lag_weeks !== undefined ? ` (${s.kickoff_lag_weeks} weeks)` : ''}
            </div>
          </div>
          <div class="fact">
            <div class="k">Delivery</div>
            <div class="v">${s.delivery_date ? mediumDate(s.delivery_date) : '—'}</div>
            <div class="s">${sourceWords(s.delivery_source)}</div>
          </div>
          <div class="fact">
            <div class="k">Duration</div>
            <div class="v">${s.duration_weekdays} weekdays</div>
            <div class="s">
              ${sourceWords(s.duration_source)}${s.duration_analog_count ? ` (${s.duration_analog_count})` : ''}
              · ${label(s.curve)} curve
            </div>
          </div>
        </div>
        ${
          s.weeks.length
            ? html`<div class="scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Week of</th>
                      <th class="num">Hours</th>
                      ${services.map((sv) => html`<th class="num">${label(sv)}</th>`)}
                    </tr>
                  </thead>
                  <tbody>
                    ${s.weeks.map(
                      (w) =>
                        html`<tr>
                          <td>${mediumDate(w.week_start)}</td>
                          <td class="num">${quarterHours(w.hours, { unit: false })}</td>
                          ${services.map((sv) => html`<td class="num">${w.by_service[sv] ? quarterHours(w.by_service[sv], { unit: false }) : ''}</td>`)}
                        </tr>`,
                    )}
                  </tbody>
                </table>
              </div>`
            : nothing
        }
      </div>
    `;
  }

  _assignments(r) {
    return html`
      <div class="card">
        <div class="eyebrow">Assignments</div>
        <h3>Who would do the work</h3>
        <p class="sub">
          The role mix is ${sourceWords(r.role_mix_source) || 'not known'}. Under each role: the
          people who logged the most hours to the analogs on that service, then the people whose
          discipline it is — advice for the producer, not an assignment.
        </p>
        ${
          r.assignments.length
            ? html`<div class="scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Role</th>
                      <th class="num">Share</th>
                      <th class="num">Hours</th>
                      <th>People</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${r.assignments.map(
                      (a) =>
                        html`<tr>
                          <td>${label(a.service)}</td>
                          <td class="num">${percent(a.share)}</td>
                          <td class="num">${quarterHours(a.hours, { unit: false })}</td>
                          <td>
                            ${
                              a.people.length
                                ? html`<ul class="people">
                                    ${a.people.map((p) => html`<li>${p.name} <span>· ${p.reason}</span></li>`)}
                                  </ul>`
                                : html`<span class="muted">Nobody active has booked this</span>`
                            }
                          </td>
                        </tr>`,
                    )}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td>Total</td>
                      <td></td>
                      <td class="num">
                        ${quarterHours(
                          r.assignments.reduce((s, a) => s + a.hours, 0),
                          { unit: false },
                        )}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>`
            : html`<p class="muted">No roles: nothing says which services would do this work.</p>`
        }
      </div>
    `;
  }

  _tasks(r) {
    const t = r.tasks;
    if (!t) return nothing;
    const analogsWithTasks = t.analog_count ?? 0;
    return html`
      <div class="card">
        <div class="eyebrow">Tasks</div>
        <h3>A typical project like this one</h3>
        ${
          t.basis === 'analogs'
            ? html`<p class="sub">
                  The phases and the tasks most of the ${count(analogsWithTasks)}
                  ${analogsWithTasks === 1 ? 'analog' : 'analogs'} with a schedule share, each
                  analog's planned hours read as a share of its own total, so their size does not
                  matter; then each role's hours above spread over the tasks in the proportions the
                  analogs booked that role to them. ${percent(t.coverage)} of the analogs' hours sit
                  in shared tasks; the rest is in the Other lines.
                </p>
                <div class="scroll">
                  <table class="tasks">
                    <thead>
                      <tr>
                        <th>Phase / task</th>
                        <th class="num">Analogs</th>
                        <th class="num">Hours</th>
                        <th>By role</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${t.phases.map(
                        (p) => html`
                          <tr class="phase">
                            <td>${p.name}</td>
                            <td class="num">${p.other ? '' : percent(p.prevalence)}</td>
                            <td class="num">${quarterHours(p.hours, { unit: false })}</td>
                            <td></td>
                          </tr>
                          ${p.tasks.map(
                            (k) =>
                              html`<tr class=${k.other ? 'other' : ''}>
                                <td class="task">${k.name}</td>
                                <td class="num">${k.other ? '' : percent(k.prevalence)}</td>
                                <td class="num">${quarterHours(k.hours, { unit: false })}</td>
                                <td class="muted">
                                  ${Object.entries(k.by_service)
                                    .map(
                                      ([svc, h]) =>
                                        `${label(svc)} ${quarterHours(h, { unit: false })}`,
                                    )
                                    .join(' · ')}
                                </td>
                              </tr>`,
                          )}
                        `,
                      )}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td>Total</td>
                        <td></td>
                        <td class="num">${quarterHours(t.hours, { unit: false })}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>`
            : html`<p class="muted">${TASKS_SKIPPED[t.reason] ?? 'No task breakdown.'}</p>`
        }
      </div>
    `;
  }

  _analogs(r) {
    return html`
      <div class="card">
        <div class="eyebrow">Analogs</div>
        <h3>
          ${count(r.analogs.length)} comparable ${r.analogs.length === 1 ? 'project' : 'projects'}
          of ${count(r.candidate_count ?? 0)}
        </h3>
        <p class="sub">
          Scored on the words of the title against the project's name, the client, the kind of work,
          the amount against what the project billed, and how recent it is — over the axes this deal
          has. A project the title names by number leads outright.
        </p>
        ${
          r.analogs.length
            ? html`<div class="scroll">
                <table>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Project</th>
                      <th>Client</th>
                      <th>Type</th>
                      <th class="num">Billed</th>
                      <th class="num">Hours</th>
                      <th class="num">Weekdays</th>
                      <th>Finished</th>
                      <th class="num">Similarity</th>
                      <th>Axes</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${r.analogs.map(
                      (a) =>
                        html`<tr>
                          <td>${a.rank}</td>
                          <td>
                            <b>${a.number ?? ''}</b>
                            ${a.name}${a.named ? html` <span class="muted">(named in the title)</span>` : nothing}
                          </td>
                          <td>${a.client ?? ''}</td>
                          <td>${a.canonical_type ? label(a.canonical_type) : '—'}</td>
                          <td class="num">${a.gross === null ? '—' : money(a.gross)}</td>
                          <td class="num">
                            ${a.hours_actual === null ? '—' : quarterHours(a.hours_actual, { unit: false })}
                          </td>
                          <td class="num">${a.duration_weekdays ?? '—'}</td>
                          <td>${a.completed_on ? mediumDate(a.completed_on) : '—'}</td>
                          <td class="num">${percent(a.similarity)}</td>
                          <td class="scores">
                            ${Object.entries(a.components)
                              .filter(([, v]) => v !== null)
                              .map(([k, v]) => `${k} ${Math.round(v * 100)}%`)
                              .join(' · ')}
                            ${a.used_for_mix ? ' · mix' : ''}
                          </td>
                        </tr>`,
                    )}
                  </tbody>
                </table>
              </div>`
            : html`<p class="muted">No closed project resembles this deal above the floor.</p>`
        }
      </div>
    `;
  }

  _activity(activities) {
    const shown = this._showAllActivity ? activities : activities.slice(0, 8);
    return html`
      <div class="card">
        <div class="eyebrow">Activity</div>
        <h3>
          ${count(activities.length)} ${activities.length === 1 ? 'engagement' : 'engagements'} in
          HubSpot
        </h3>
        ${
          activities.length
            ? html`<ul class="activity">
                  ${shown.map(
                    (a) =>
                      html`<li>
                        <div class="head">
                          <b>${KIND_WORDS[a.kind] ?? a.kind}</b>
                          <span
                            >${a.occurred_at ? mediumDate(a.occurred_at.slice(0, 10)) : ''}</span
                          >
                          ${a.owner_name ? html`<span>${a.owner_name}</span>` : nothing}
                          ${a.direction ? html`<span>${label(a.direction).toLowerCase()}</span>` : nothing}
                        </div>
                        ${a.subject ? html`<div><b>${a.subject}</b></div>` : nothing}
                        ${a.preview ? html`<div class="preview">${a.preview}</div>` : nothing}
                      </li>`,
                  )}
                </ul>
                ${
                  activities.length > shown.length
                    ? html`<button
                        class="secondary"
                        style="margin-top:10px"
                        @click=${() => (this._showAllActivity = true)}
                      >
                        Show all ${count(activities.length)}
                      </button>`
                    : nothing
                }`
            : html`<p class="muted">
                Nothing logged against this deal — or the sync's token cannot read engagements yet.
              </p>`
        }
      </div>
    `;
  }

  _versions(versions, current) {
    return html`
      <div class="card">
        <div class="eyebrow">Versions</div>
        <h3>Every generation, kept</h3>
        <ul class="versions">
          ${versions.map(
            (v) =>
              html`<li class=${v.id === (this._viewing?.id ?? current?.id) ? 'current' : ''}>
                <button @click=${() => this._view(v)}>Version ${v.version}</button>
                <span class="muted">
                  · ${since(v.generated_at)} ·
                  ${v.trigger === 'sync' ? 'by the sync' : v.trigger === 'kickoff' ? 'kick-off date changed' : (v.generated_by ?? 'by hand')}
                  · ${v.total_hours === null ? 'no hours' : quarterHours(v.total_hours)}
                  ${v.kickoff_date ? html`· kick-off ${mediumDate(v.kickoff_date)}` : nothing}
                </span>
              </li>`,
          )}
        </ul>
      </div>
    `;
  }

  render() {
    if (this._loadError)
      return html`<div class="card"><p class="error">${this._loadError}</p></div>`;
    if (!this._data) return html`<div class="card"><p class="muted">Loading…</p></div>`;
    const { deal, estimate, versions, activities } = this._data;
    const shown = this._viewing ?? estimate;
    const r = shown?.result ?? null;
    return html`
      ${this._header(deal)}
      ${
        this._viewing
          ? html`<div class="banner note">
              Reading version ${this._viewing.version}, generated
              ${since(this._viewing.generated_at)}.
              <button
                class="secondary"
                style="margin-left:10px"
                @click=${() => (this._viewing = null)}
              >
                Back to the current version
              </button>
            </div>`
          : nothing
      }
      ${this._kickoffCard(deal)}
      ${
        r
          ? html`
              <div class="grid2">${this._scope(r)} ${this._typeCard(r)}</div>
              ${this._schedule(r)} ${this._assignments(r)} ${this._tasks(r)} ${this._analogs(r)}
            `
          : nothing
      }
      <div class="grid2">
        ${this._activity(activities)}
        ${versions.length ? this._versions(versions, estimate) : nothing}
      </div>
    `;
  }
}

customElements.define('estimate-deal', EstimateDeal);
